import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  DollarSign,
  User,
  Store,
  Truck,
  Loader2
} from 'lucide-react';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { DisputeEvidenceViewer } from '../../components/admin/DisputeEvidenceViewer';

interface Dispute {
  id: string;
  orderId: string;
  type: 'not_received' | 'wrong_items' | 'damaged' | 'late_delivery' | 'other';
  status: 'open' | 'investigating' | 'resolved' | 'denied';
  priority: 'low' | 'medium' | 'high';
  reporterRole: string;
  customerName: string;
  businessId: string;
  businessName: string;
  driverId: string | null;
  amount: number;
  description: string;
  createdAt: Date;
  updatedAt: Date;
  resolution?: {
    kind: string;
    refundAmount: number | null;
    notes: string;
  };
}

function toDate(value: any): Date {
  if (!value) return new Date(0);
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

function mapDisputeDoc(id: string, data: any): Dispute {
  return {
    id,
    orderId: data.orderId || '',
    type: data.type || 'other',
    status: data.status || 'open',
    priority: data.priority || (data.reporterRole === 'driver' ? 'high' : 'medium'),
    reporterRole: data.reporterRole || 'shopper',
    customerName: data.customerName || (data.reporterRole === 'driver' ? 'Driver report' : 'Customer'),
    businessId: data.businessId || '',
    businessName: data.businessName || '—',
    driverId: data.driverId || (data.reporterRole === 'driver' ? data.reporterId : null),
    amount: data.amount || 0,
    description: data.description || [data.reason, data.details].filter(Boolean).join(' — ') || 'No description',
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt || data.createdAt),
    resolution: data.resolution || undefined,
  };
}

const typeLabels: Record<string, { label: string; icon: React.ElementType }> = {
  not_received: { label: 'Not Received', icon: XCircle },
  wrong_items: { label: 'Wrong Items', icon: AlertTriangle },
  damaged: { label: 'Damaged', icon: AlertTriangle },
  late_delivery: { label: 'Late Delivery', icon: Clock },
  other: { label: 'Other', icon: MessageSquare },
};

const statusConfig: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
  open: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  investigating: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  resolved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  denied: { bg: 'bg-gray-100', text: 'text-gray-600', icon: XCircle },
};

const priorityConfig: Record<string, { bg: string; text: string }> = {
  low: { bg: 'bg-gray-100', text: 'text-gray-600' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700' },
  high: { bg: 'bg-red-100', text: 'text-red-700' },
};

interface DisputeDetailPanelProps {
  dispute: Dispute;
  onClose: () => void;
}

function DisputeDetailPanel({ dispute, onClose }: DisputeDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'evidence' | 'resolve'>('details');
  const [orderData, setOrderData] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [partialAmount, setPartialAmount] = useState('');
  const [showPartialInput, setShowPartialInput] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const statusStyle = statusConfig[dispute.status];
  const StatusIcon = statusStyle.icon;
  const typeInfo = typeLabels[dispute.type];
  const TypeIcon = typeInfo.icon;
  const isClosed = dispute.status === 'resolved' || dispute.status === 'denied';

  // Pull the order for evidence (proof photo, PIN verification, GPS)
  useEffect(() => {
    if (!dispute.orderId) return;
    getDoc(doc(db, 'orders', dispute.orderId))
      .then((snap) => setOrderData(snap.exists() ? snap.data() : null))
      .catch((err) => console.error('[AdminDisputes] order fetch error:', err));
  }, [dispute.orderId]);

  const evidence = useMemo(() => {
    const items: any[] = [];
    if (orderData?.deliveryProof?.photoUrl) {
      items.push({
        id: 'proof',
        type: 'photo' as const,
        title: 'Delivery Proof Photo',
        description: orderData.deliveryProof.notes || 'Photo taken by driver at delivery',
        data: {
          url: orderData.deliveryProof.photoUrl,
          metadata: {
            capturedAt: toDate(orderData.deliveryProof.capturedAt),
          },
        },
        timestamp: toDate(orderData.deliveryProof.capturedAt),
      });
    }
    if (orderData?.pickup?.verifiedAt || orderData?.pickedUpAt) {
      items.push({
        id: 'pin',
        type: 'verification' as const,
        title: 'PIN Verification',
        description: 'Pickup code verification at the boutique',
        data: {
          verified: true,
          attempts: [{
            success: true,
            time: toDate(orderData.pickup?.verifiedAt || orderData.pickedUpAt).toLocaleTimeString(),
          }],
        },
        timestamp: toDate(orderData.pickup?.verifiedAt || orderData.pickedUpAt),
      });
    }
    if (orderData?.driverLocation?.lat != null) {
      items.push({
        id: 'gps',
        type: 'gps' as const,
        title: 'Last Driver Location',
        description: 'Most recent GPS position reported by the courier',
        data: {
          pickup: orderData.pickupAddress?.street || 'Boutique',
          pickupTime: orderData.pickedUpAt ? toDate(orderData.pickedUpAt).toLocaleTimeString() : '—',
          dropoff: `${orderData.driverLocation.lat.toFixed(4)}, ${orderData.driverLocation.lng.toFixed(4)}`,
          dropoffTime: orderData.deliveredAt ? toDate(orderData.deliveredAt).toLocaleTimeString() : '—',
        },
        timestamp: toDate(orderData.driverLocation.updatedAt),
      });
    }
    return items;
  }, [orderData]);

  const callResolve = async (resolution: 'refund' | 'deny', refundAmount?: number) => {
    if (!notes.trim()) {
      setError('Resolution notes are required');
      return;
    }
    setBusy(resolution + (refundAmount !== undefined ? '_partial' : ''));
    setError(null);
    try {
      const resolve = httpsCallable(functions, 'resolveDispute');
      await resolve({
        disputeId: dispute.id,
        resolution,
        ...(refundAmount !== undefined ? { refundAmount } : {}),
        notes: notes.trim(),
      });
      setSuccess(resolution === 'refund' ? 'Refund processed — dispute resolved.' : 'Dispute denied — order completed.');
    } catch (err: any) {
      setError(err?.message || 'Failed to resolve dispute');
    } finally {
      setBusy(null);
    }
  };

  const startInvestigation = async () => {
    setBusy('investigate');
    setError(null);
    try {
      const start = httpsCallable(functions, 'startDisputeInvestigation');
      await start({ disputeId: dispute.id });
    } catch (err: any) {
      setError(err?.message || 'Failed to update dispute');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-serif text-heading">Dispute #{dispute.id.slice(-4).toUpperCase()}</h2>
                <span className={cn("px-2 py-0.5 rounded text-xs font-medium uppercase", priorityConfig[dispute.priority].bg, priorityConfig[dispute.priority].text)}>
                  {dispute.priority}
                </span>
              </div>
              <p className="text-sm text-body mt-1">
                Order #{dispute.orderId.slice(-6).toUpperCase()} • ${dispute.amount.toFixed(2)}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className={cn("px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1", statusStyle.bg, statusStyle.text)}>
                <StatusIcon className="h-4 w-4" />
                <span className="capitalize">{dispute.status}</span>
              </span>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XCircle className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-100">
          <div className="flex space-x-6">
            {[
              { id: 'details', label: 'Details' },
              { id: 'evidence', label: `Evidence (${evidence.length})` },
              { id: 'resolve', label: 'Resolve' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "py-3 text-sm font-medium border-b-2 transition-colors",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-body hover:text-heading"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {(error || success) && (
            <div className={cn(
              "mb-4 p-3 rounded-xl text-sm flex items-start gap-2",
              success
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-red-50 border border-red-200 text-red-700"
            )}>
              {success ? <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
              {success || error}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Type & Description */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center space-x-2">
                    <TypeIcon className="h-5 w-5 text-body" />
                    <span>{typeInfo.label}</span>
                    <span className="text-xs font-normal text-body">
                      (reported by {dispute.reporterRole})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-body">{dispute.description}</p>
                  <div className="mt-4 flex items-center space-x-4 text-sm text-body">
                    <span>Opened: {dispute.createdAt.toLocaleDateString()}</span>
                    <span>•</span>
                    <span>Updated: {dispute.updatedAt.toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Parties Involved */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <User className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-body">Customer</p>
                        <p className="font-medium text-heading">{dispute.customerName}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Store className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-body">Business</p>
                        <p className="font-medium text-heading">{dispute.businessName}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Truck className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-body">Driver</p>
                        {dispute.driverId ? (
                          <p className="font-medium text-heading">#{dispute.driverId.slice(-6)}</p>
                        ) : (
                          <p className="text-body text-sm">Not assigned</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Order snapshot */}
              {orderData && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Order Snapshot</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-body">Current state</span>
                      <span className="font-medium text-heading">{orderData.state?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-body">Items</span>
                      <span className="font-medium text-heading">{(orderData.items || []).length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-body">Total</span>
                      <span className="font-medium text-heading">${(orderData.total || 0).toFixed(2)}</span>
                    </div>
                    {orderData.deliveredAt && (
                      <div className="flex justify-between">
                        <span className="text-body">Delivered</span>
                        <span className="font-medium text-heading">{toDate(orderData.deliveredAt).toLocaleString()}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Resolution (if any) */}
              {dispute.resolution && (
                <Card className={dispute.status === 'resolved' ? "border-green-200 bg-green-50/50" : "border-gray-200 bg-gray-50"}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Resolution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-body">
                      {dispute.resolution.kind === 'refund'
                        ? `Refunded $${(dispute.resolution.refundAmount || 0).toFixed(2)} — `
                        : 'Claim denied — '}
                      {dispute.resolution.notes}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'evidence' && (
            evidence.length > 0 ? (
              <DisputeEvidenceViewer
                disputeId={dispute.id}
                evidence={evidence}
                onRefund={() => setActiveTab('resolve')}
                onPartialRefund={() => setActiveTab('resolve')}
                onDeny={() => setActiveTab('resolve')}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-heading font-medium">No evidence on file</p>
                  <p className="text-body text-sm mt-1">
                    This order has no delivery proof photo, PIN verification, or GPS data yet.
                  </p>
                </CardContent>
              </Card>
            )
          )}

          {activeTab === 'resolve' && (
            <div className="space-y-6">
              {isClosed ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="h-10 w-10 text-green-300 mx-auto mb-3" />
                    <p className="text-heading font-medium">This dispute is closed</p>
                    <p className="text-body text-sm mt-1 capitalize">Status: {dispute.status}</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Resolution Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-heading mb-2">
                        Resolution Notes <span className="text-error">*</span>
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Explain the resolution decision..."
                        rows={4}
                        className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light resize-none"
                      />
                    </div>

                    {showPartialInput && (
                      <div>
                        <label className="block text-sm font-medium text-heading mb-2">
                          Partial Refund Amount (max ${dispute.amount.toFixed(2)})
                        </label>
                        <div className="flex gap-3">
                          <Input
                            type="number"
                            min="0.01"
                            max={dispute.amount}
                            step="0.01"
                            value={partialAmount}
                            onChange={(e) => setPartialAmount(e.target.value)}
                            placeholder="0.00"
                            className="max-w-[160px]"
                          />
                          <Button
                            onClick={() => callResolve('refund', parseFloat(partialAmount))}
                            disabled={!partialAmount || parseFloat(partialAmount) <= 0 || parseFloat(partialAmount) > dispute.amount || !notes.trim()}
                            loading={busy === 'refund_partial'}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            Refund ${partialAmount || '0.00'}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-3">
                      {dispute.status === 'open' && (
                        <Button
                          variant="outline"
                          onClick={startInvestigation}
                          loading={busy === 'investigate'}
                        >
                          <Clock className="h-4 w-4 mr-2" />
                          Start Investigation
                        </Button>
                      )}
                      <Button
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => callResolve('refund')}
                        disabled={!notes.trim() || dispute.amount <= 0}
                        loading={busy === 'refund'}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Full Refund (${dispute.amount.toFixed(2)})
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowPartialInput(!showPartialInput)}
                      >
                        <DollarSign className="h-4 w-4 mr-2" />
                        Partial Refund
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:border-red-300"
                        onClick={() => callResolve('deny')}
                        disabled={!notes.trim()}
                        loading={busy === 'deny'}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Deny Claim
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminDisputes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedDisputeId, setSelectedDisputeId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'disputes'), orderBy('createdAt', 'desc'), limit(100));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDisputes(snap.docs.map((d) => mapDisputeDoc(d.id, d.data())));
        setLoading(false);
      },
      (err) => {
        console.error('[AdminDisputes] subscription error:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const selectedDispute = useMemo(
    () => disputes.find((d) => d.id === selectedDisputeId) || null,
    [disputes, selectedDisputeId]
  );

  const filteredDisputes = disputes.filter(dispute => {
    const matchesSearch = 
      dispute.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dispute.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dispute.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || dispute.status === statusFilter;
    const matchesType = typeFilter === 'all' || dispute.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const openCount = disputes.filter(d => d.status === 'open').length;
  const investigatingCount = disputes.filter(d => d.status === 'investigating').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Disputes</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">Review and resolve customer disputes</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          {openCount > 0 && (
            <div className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm">
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{openCount} open</span>
            </div>
          )}
          {investigatingCount > 0 && (
            <div className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs sm:text-sm">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{investigatingCount} investigating</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Total Disputes', value: disputes.length, icon: AlertTriangle, color: 'bg-red-500' },
          { label: 'Open', value: openCount, icon: Clock, color: 'bg-amber-500' },
          { label: 'Resolved', value: disputes.filter(d => d.status === 'resolved').length, icon: CheckCircle, color: 'bg-green-500' },
          { label: 'Total Value', value: `$${disputes.reduce((sum, d) => sum + d.amount, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: DollarSign, color: 'bg-purple-500' },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-3 sm:p-4 flex items-center space-x-3 sm:space-x-4">
              <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white flex-shrink-0", stat.color)}>
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-body truncate">{stat.label}</p>
                <p className="text-lg sm:text-2xl font-bold text-heading">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <Input
                placeholder="Search disputes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 sm:pl-10 text-sm h-9 sm:h-10"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 h-9 sm:h-10 text-sm"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={cn("h-4 w-4 transition-transform", showFilters && "rotate-180")} />
            </Button>
          </div>

          {showFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 grid sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Status</label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'open', 'investigating', 'resolved', 'denied'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={cn(
                        "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors capitalize",
                        statusFilter === status
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-body hover:bg-gray-200"
                      )}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Type</label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'not_received', 'wrong_items', 'damaged', 'late_delivery', 'other'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      className={cn(
                        "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors",
                        typeFilter === type
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-body hover:bg-gray-200"
                      )}
                    >
                      {type === 'all' ? 'All' : typeLabels[type]?.label || type}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disputes List */}
      <div className="space-y-3 sm:space-y-4">
        {filteredDisputes.map((dispute) => {
          const statusStyle = statusConfig[dispute.status];
          const StatusIcon = statusStyle.icon;
          const typeInfo = typeLabels[dispute.type];
          const TypeIcon = typeInfo.icon;
          const priorityStyle = priorityConfig[dispute.priority];

          return (
            <Card 
              key={dispute.id} 
              className={cn(
                "hover:shadow-lg transition-shadow cursor-pointer",
                dispute.priority === 'high' && dispute.status === 'open' && "border-red-200"
              )}
              onClick={() => setSelectedDisputeId(dispute.id)}
            >
              <CardContent className="p-3 sm:p-4 lg:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className={cn(
                      "w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0",
                      dispute.status === 'open' ? "bg-red-100" : 
                      dispute.status === 'investigating' ? "bg-amber-100" : "bg-gray-100"
                    )}>
                      <TypeIcon className={cn(
                        "h-5 w-5 sm:h-6 sm:w-6",
                        dispute.status === 'open' ? "text-red-600" : 
                        dispute.status === 'investigating' ? "text-amber-600" : "text-gray-500"
                      )} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
                        <h3 className="font-semibold text-heading text-sm sm:text-base">#{dispute.id.slice(-4).toUpperCase()}</h3>
                        <span className={cn("px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium uppercase", priorityStyle.bg, priorityStyle.text)}>
                          {dispute.priority}
                        </span>
                        {/* Mobile status badge */}
                        <span className={cn("sm:hidden px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center space-x-1", statusStyle.bg, statusStyle.text)}>
                          <StatusIcon className="h-2.5 w-2.5" />
                          <span className="capitalize">{dispute.status}</span>
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-body mt-0.5 sm:mt-1">{typeInfo.label} • Order #{dispute.orderId.slice(-6).toUpperCase()}</p>
                      <p className="text-xs sm:text-sm text-body mt-1 sm:mt-2 line-clamp-2">{dispute.description}</p>
                      <div className="flex items-center flex-wrap gap-2 sm:gap-4 mt-2 sm:mt-3 text-xs sm:text-sm text-body">
                        <span className="flex items-center space-x-1">
                          <User className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="truncate max-w-[100px]">{dispute.customerName}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Store className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="truncate max-w-[100px]">{dispute.businessName}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-0 pt-2 sm:pt-0 border-t sm:border-0 border-gray-100">
                    {/* Desktop status badge */}
                    <span className={cn("hidden sm:flex px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium items-center space-x-1", statusStyle.bg, statusStyle.text)}>
                      <StatusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="capitalize">{dispute.status}</span>
                    </span>
                    <p className="text-base sm:text-lg font-bold text-heading sm:mt-2">${dispute.amount.toFixed(2)}</p>
                    <p className="text-[10px] sm:text-xs text-body sm:mt-1">{dispute.createdAt.toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredDisputes.length === 0 && (
        <Card>
          <CardContent className="py-8 sm:py-12 text-center">
            <CheckCircle className="h-10 w-10 sm:h-12 sm:w-12 text-green-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-heading font-medium text-sm sm:text-base">No disputes found</p>
            <p className="text-body text-xs sm:text-sm mt-1">
              {disputes.length === 0 ? 'All clear! No disputes have been filed.' : 'No matching disputes.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Dispute Detail Modal */}
      {selectedDispute && (
        <DisputeDetailPanel 
          dispute={selectedDispute} 
          onClose={() => setSelectedDisputeId(null)}
        />
      )}
    </div>
  );
}
